(() => {
  if (customElements.get('shipping-bar')) {
    return;
  }

  /**
   * Shipping Bar component.
   */
  class ShippingBar extends HTMLElement {
    /**
     * Constructor.
     */
    constructor() {
      super();
      this.currentCurrency = Shopify.currency.active;
      this.conversionRate = Shopify.currency.rate;
      this.moneyFormat = this.dataset.moneyFormat;

      this.threshold = Number(this.dataset.threshold);
      this.thresholdType = this.dataset.thresholdType;
      this.thresholdSecondary = Number(this.dataset.thresholdSecondary);
      this.thresholdSecondaryType = this.dataset.thresholdSecondaryType;
      this.thresholdTertiary = Number(this.dataset.thresholdTertiary);
      this.thresholdTertiaryType = this.dataset.thresholdTertiaryType;
      this.thresholdQuaternary = Number(this.dataset.thresholdQuaternary);
      this.thresholdQuaternaryType = this.dataset.thresholdQuaternaryType;


      this.convertedThreshold = 0;

      this.selectors = {
        progressBar: '[data-progress-bar]',
      };
      this.lastThresholdReached = false;

      if (this.threshold === 0) {
        const primaryIconProgressBar = this.querySelector('.progress-bar__icon--primary');
        if (primaryIconProgressBar) {
          primaryIconProgressBar.classList.add('is_no_value');
        }

        const progressBarDetails = this.querySelector('.progress-bar__details');
        if (progressBarDetails) {
          progressBarDetails.classList.add('first_icon_no_value');
        }
      }

      this.maxThreshold = this.threshold;
      this.maxThresholdType = this.thresholdType;
      if (this.thresholdSecondary) {
        this.maxThreshold = this.thresholdSecondary;
        this.maxThresholdType = this.thresholdSecondaryType;
      }
      if (this.thresholdTertiary) {
        this.maxThreshold = this.thresholdTertiary;
        this.maxThresholdType = this.thresholdTertiaryType;
      }
      if (this.thresholdQuaternary) {
        this.maxThreshold = this.thresholdQuaternary;
        this.maxThresholdType = this.thresholdQuaternaryType;
      }
    }

    /**
     * Show success.
     */
    showSuccess() {
      this.progressBar.style.width = '100%';
      this.classList.add('has-reached-quaternary');
    }

    /**
     * Calculate converted prices.
     */
    calculateConvertedPrices() {
      if (this.thresholdType === 'value') {
        this.threshold = (this.threshold * (this.conversionRate || 1)).toFixed(0);
      }
      if (this.thresholdSecondaryType === 'value') {
        this.thresholdSecondary = (this.thresholdSecondary * (this.conversionRate || 1)).toFixed(0);
      }
      if (this.thresholdTertiaryType === 'value') {
        this.thresholdTertiary = (this.thresholdTertiary * (this.conversionRate || 1)).toFixed(0);
      }
      if (this.thresholdQuaternaryType === 'value') {
        this.thresholdQuaternary = (this.thresholdQuaternary * (this.conversionRate || 1)).toFixed(0);
      }
    }

    /**
     * Show progress.
     */
    showProgress() {
      this.updateProgressPercent();

      const { threshold } = this.getReachedThreshold();
      this.classList.add(`has-reached-${threshold}`);
    }

    /**
     * Get reached threshold.
     *
     * @returns {Object<threshold: String, amount: Number>}
     */
    getReachedThreshold() {
      // Compter le nombre de paliers actifs
      const hasQuaternary = this.thresholdQuaternary && this.thresholdQuaternary > 0;
      const hasTertiary = this.thresholdTertiary && this.thresholdTertiary > 0;
      const hasSecondary = this.thresholdSecondary && this.thresholdSecondary > 0;

      let activeThresholds = 1; // Primary est toujours actif
      if (hasSecondary) activeThresholds++;
      if (hasTertiary) activeThresholds++;
      if (hasQuaternary) activeThresholds++;

      // Index du dernier palier = nombre de paliers - 1
      const maxIndex = activeThresholds - 1;

      if (this.maxThresholdReached) {
        const thresholdName = hasQuaternary ? 'quaternary' : (hasTertiary ? 'tertiary' : (hasSecondary ? 'secondary' : 'primary'));
        return { threshold: thresholdName, amount: this.maxThreshold, index: maxIndex };
      }

      if (
        hasTertiary &&
        ((this.thresholdTertiaryType === 'value' && this.totalPrice >= this.thresholdTertiary) ||
        (this.thresholdTertiaryType === 'count' && this.totalItems >= this.thresholdTertiary))
      ) {
        return {
          threshold: 'tertiary',
          amount: this.thresholdQuaternary || this.maxThreshold,
          index: hasQuaternary ? 2 : maxIndex,
        };
      }
      if (
        hasSecondary &&
        ((this.thresholdSecondaryType === 'value' && this.totalPrice >= this.thresholdSecondary) ||
        (this.thresholdSecondaryType === 'count' && this.totalItems >= this.thresholdSecondary))
      ) {
        return {
          threshold: 'secondary',
          amount: this.thresholdTertiary || this.maxThreshold,
          index: (hasTertiary || hasQuaternary) ? 1 : maxIndex,
        };
      }
      if (
        (this.thresholdType === 'value' && this.totalPrice >= this.threshold) ||
        (this.thresholdType === 'count' && this.totalItems >= this.threshold)
      ) {
        return {
          threshold: 'primary',
          amount: this.thresholdSecondary || this.maxThreshold,
          index: 0,
        };
      }

      return { threshold: 'none', amount: this.threshold, index: -1 };
    }

    /**
     * Update progress percent.
     */
    updateProgressPercent() {
      // Déterminer le nombre de paliers actifs
      const hasQuaternary = this.thresholdQuaternary && this.thresholdQuaternary > 0;
      const hasTertiary = this.thresholdTertiary && this.thresholdTertiary > 0;
      const hasSecondary = this.thresholdSecondary && this.thresholdSecondary > 0;

      // Positions des icônes selon le nombre de paliers
      let iconPositions = [100]; // Par défaut, un seul palier à 100%

      if (hasQuaternary) {
        iconPositions = [25, 50, 75, 100];
      } else if (hasTertiary) {
        iconPositions = [50, 75, 100];
      } else if (hasSecondary) {
        iconPositions = [50, 100];
      }

      // Seuils correspondants
      const thresholds = [this.threshold];
      const thresholdTypes = [this.thresholdType];

      if (hasSecondary) {
        thresholds.push(this.thresholdSecondary);
        thresholdTypes.push(this.thresholdSecondaryType);
      }
      if (hasTertiary) {
        thresholds.push(this.thresholdTertiary);
        thresholdTypes.push(this.thresholdTertiaryType);
      }
      if (hasQuaternary) {
        thresholds.push(this.thresholdQuaternary);
        thresholdTypes.push(this.thresholdQuaternaryType);
      }

      // Valeur actuelle (prix ou items)
      const currentValue = thresholdTypes[0] === 'value' ? this.totalPrice : this.totalItems;

      // Calculer le pourcentage de progression
      let progressPercent = 0;

      // Trouver dans quel segment on se trouve
      for (let i = 0; i < thresholds.length; i++) {
        if (currentValue >= thresholds[i]) {
          // On a atteint ou dépassé ce seuil
          progressPercent = iconPositions[i];
        } else {
          // On est entre deux seuils
          if (i === 0) {
            // Entre 0 et le premier seuil
            progressPercent = (currentValue / thresholds[0]) * iconPositions[0];
          } else {
            // Entre deux seuils
            const prevThreshold = thresholds[i - 1];
            const nextThreshold = thresholds[i];
            const prevPosition = iconPositions[i - 1];
            const nextPosition = iconPositions[i];

            const segmentProgress = (currentValue - prevThreshold) / (nextThreshold - prevThreshold);
            progressPercent = prevPosition + (segmentProgress * (nextPosition - prevPosition));
          }
          break;
        }
      }

      this.progressBar.style.width = `${progressPercent}%`;
      this.progressBar.parentElement.classList.remove('hidden');
    }

    /**
     * Get formatted price.
     *
     * @param {String} price
     * @returns {String}
     */
    getFormattedPrice(price) {
      return Shopify.formatMoney(price, this.moneyFormat);
    }

    /**
     * Cache elements.
     */
    cacheElements() {
      this.progressBar = this.querySelector(this.selectors.progressBar);
    }

    init() {
      this.totalPrice = Number(this.dataset.totalPrice);
      this.totalItems = Number(this.dataset.totalItems);
      ['has-reached-primary', 'has-reached-secondary', 'has-reached-tertiary', 'has-reached-quaternary'].forEach(
        (stateClass) => {
          this.classList.remove(stateClass);
        }
      );

      this.calculateConvertedPrices();
      const thresholdIsPassed =
        (this.maxThresholdType === 'value' && this.totalPrice >= this.maxThreshold) ||
        (this.maxThresholdType === 'count' && this.totalItems >= this.maxThreshold);
      if (!thresholdIsPassed && this.maxThreshold > 0) {
        this.maxThresholdReached = false;
        this.showProgress();
        return;
      }

      this.maxThresholdReached = true;
      this.showSuccess();
    }

    /**
     * Connected callback.
     *
     * @returns {Void}
     */
    connectedCallback() {
      this.cacheElements();
      this.init();
    }
  }

  customElements.define('shipping-bar', ShippingBar);
})();
